import React, { useState, useCallback } from 'react';
import {
    Box,
    Modal,
    IconButton,
    Typography,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon
} from '@mui/icons-material';
import FactorsDisplay from './FactorsDisplay';
import { SeatRecommendation } from '../../models/seat';

export interface SeatFactorModalState {
    open: boolean;
    seatData: SeatRecommendation | null;
}

export interface SeatFactorModalProps {
    modalState: SeatFactorModalState;
    onClose: () => void;
}

/**
 * Custom hook for seat factor modal state management
 * Provides state management for opening/closing the modal with seat data
 */
export const useSeatFactorModal = () => {
    const [modalState, setModalState] = useState<SeatFactorModalState>({
        open: false,
        seatData: null
    });

    /**
     * Opens the modal with the selected seat's factor data
     */
    const openModal = useCallback((seatData: SeatRecommendation) => {
        setModalState({
            open: true,
            seatData
        });
    }, []);

    /**
     * Closes the modal and resets state
     */
    const closeModal = useCallback(() => {
        setModalState({
            open: false,
            seatData: null
        });
    }, []);

    return {
        modalState,
        openModal,
        closeModal
    };
};

/**
 * SeatFactorModal component that displays positive and risk factors for a seat
 * Follows the same pattern as VenueMapModal for consistency
 */
const SeatFactorModal: React.FC<SeatFactorModalProps> = ({ modalState, onClose }) => {
    const { seatData } = modalState;
    const positiveFactors = seatData?.factors?.positiveFactors || [];
    const riskFactors = seatData?.factors?.riskFactors || [];
    const hasFactors = positiveFactors.length > 0 || riskFactors.length > 0;

    return (
        <Modal
            open={modalState.open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 3,
                    maxWidth: { xs: '90vw', sm: '600px', md: '700px' },
                    maxHeight: '90vh',
                    overflow: 'auto',
                    outline: 'none',
                    minWidth: { xs: '90vw', sm: 400 },
                    minHeight: 200
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'grey.500',
                        bgcolor: 'background.paper',
                        '&:hover': {
                            bgcolor: 'grey.100'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {seatData && (
                    <>
                        <Typography variant="h6" gutterBottom sx={{ pr: 5, mb: 2 }}>
                            Seat Factor Analysis
                        </Typography>

                        <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {seatData.section} - Row {seatData.row}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Seats: {seatData.seatRange} | Quantity: {seatData.quantity}
                            </Typography>
                        </Box>
                        { !seatData.lotId &&
                            <Alert severity="info">
                                <Typography variant="body2" color="textSecondary">
                                    Positive: <span style={{ color: 'green', fontWeight: 'bold' }}>{seatData.scores ? seatData.scores.totalPositiveScore : 0}%</span> | Risk: <span style={{ color: 'orange', fontWeight: 'bold' }}>{seatData.scores ? seatData.scores.totalRiskScore : 0}%</span>
                                </Typography>
                            </Alert>
                        }
                        {!hasFactors && (
                            <Alert severity="info">
                                No factor analysis available for this seat.
                            </Alert>
                        )}

                        <FactorsDisplay
                            positiveFactors={positiveFactors}
                            riskFactors={riskFactors}
                        />
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default SeatFactorModal;
